import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { SearchLg } from '@untitled-ui/icons-react'
import { MyButton, MyLoadingData } from '@interstellar-component'

import { WalletService } from '../../wallet.service'
import {
  formatRp,
  formatSignedRp,
  formatDateTimeParts,
  txTypeLabel,
  txLegLabel,
  txStatusBadge,
  txSign,
  TX_STATUS_TABS,
} from '../../wallet.format'

const PAGE_SIZE = 10

function StatusBadge({ status }) {
  const { cls, label } = txStatusBadge(status)
  return (
    <span
      className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-xs-medium ${cls}`}
    >
      {label}
    </span>
  )
}

StatusBadge.propTypes = { status: PropTypes.string }
StatusBadge.defaultProps = { status: null }

/**
 * "Saldo usage history" — the BR-SALDO-26 statement.
 *
 * A LEDGER view: one row per movement of SPENDABLE saldo, not one row per
 * enquiry. That is forced by the "Ending balance" column, which only has a
 * meaning per movement — an enquiry that was held then released has two
 * different ending balances and cannot honestly be collapsed into one line.
 *
 * So a CANCELLED enquiry still appears twice, as -X then +X: the reservation
 * and its return, each explaining a change the customer could watch happen to
 * their balance. A COMPLETED one appears exactly ONCE. It used to appear twice —
 * the hold that took the money and the settlement row that only cleared the held
 * bucket, rendering as two identical "Credit report / -Rp60.000 / Success /
 * Rp940.000" lines — until the server stopped listing settlement legs (STATEMENT
 * RULE in core/service/wallet/tx-presentation.js). Nothing is filtered here: the
 * tabs and this unfiltered view read whatever that one query returns.
 */
function SaldoHistory({ companyId, refreshKey }) {
  const [rows, setRows] = useState([])
  const [paginator, setPaginator] = useState(null)
  const [status, setStatus] = useState(null)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Debounce the search box so typing does not fire a request per keystroke.
  const debounceRef = useRef(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setQuery(search.trim())
      setPage(1)
    }, 350)
    return () => clearTimeout(debounceRef.current)
  }, [search])

  const load = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    setError(null)
    try {
      const res = await WalletService.getTransactions(companyId, {
        page,
        limit: PAGE_SIZE,
        status: status || undefined,
        q: query || undefined,
      })
      // SAW returns { data, paginator } and the tenant proxies it verbatim, so
      // the envelope is one level deep. Do NOT fall back to the wrapper — that
      // would surface `status: 200` as a row.
      const body = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res
      setRows(Array.isArray(body?.data) ? body.data : [])
      setPaginator(body?.paginator || null)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load saldo history')
      setRows([])
      setPaginator(null)
    } finally {
      setLoading(false)
    }
  }, [companyId, page, status, query])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  const totalPages = paginator?.total_pages || 0
  const total = paginator?.total ?? 0

  return (
    <section className="overflow-hidden rounded-xl border border-gray/200 bg-white shadow-xs">
      {/* ── Card header ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-5 py-4">
        <h3 className="text-lg-semibold text-gray/900">Saldo usage history</h3>
        <span className="rounded-full border border-brand/200 bg-brand/50 px-2 py-0.5 text-xs-medium text-brand/700">
          {total.toLocaleString('id-ID')} item
        </span>
      </div>

      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pb-4">
        <label className="relative flex w-full max-w-xs items-center">
          <SearchLg className="pointer-events-none absolute left-3 size-5 text-gray/400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by code or note"
            className="w-full rounded-lg border border-gray/300 bg-white py-2 pl-10 pr-3 text-sm-regular text-gray/900 placeholder:text-gray/500 focus:border-brand/300 focus:outline-none"
          />
        </label>

        <div className="flex items-center gap-1 rounded-lg border border-gray/200 bg-gray/50 p-1">
          {TX_STATUS_TABS.map((tab) => {
            const active = status === tab.value
            return (
              <button
                key={tab.label}
                type="button"
                onClick={() => {
                  setStatus(tab.value)
                  setPage(1)
                }}
                className={`rounded-md px-3 py-1.5 text-sm-semibold transition-colors ${
                  active
                    ? 'bg-white text-gray/900 shadow-xs'
                    : 'text-gray/600 hover:text-gray/900'
                }`}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────── */}
      <div className="overflow-x-auto border-t border-gray/200">
        <table className="w-full min-w-[860px] border-collapse">
          <thead>
            <tr className="border-b border-gray/200 bg-gray/50">
              <th className="px-5 py-3 text-left text-xs-medium text-gray/600">Category</th>
              <th className="px-5 py-3 text-left text-xs-medium text-gray/600">ID</th>
              <th className="px-5 py-3 text-left text-xs-medium text-gray/600">Date</th>
              <th className="px-5 py-3 text-right text-xs-medium text-gray/600">Amount</th>
              <th className="px-5 py-3 text-left text-xs-medium text-gray/600">Status</th>
              <th className="px-5 py-3 text-right text-xs-medium text-gray/600">Ending balance</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-5 py-12">
                  <MyLoadingData />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-sm-medium text-error/700">
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-sm-regular text-gray/500">
                  {status === 'pending'
                    ? 'No transactions awaiting payment yet.'
                    : query
                      ? `No transactions match "${query}".`
                      : 'No saldo usage history yet.'}
                </td>
              </tr>
            ) : (
              rows.map((tx) => {
                const when = formatDateTimeParts(tx.occurred_at)
                const negative = txSign(tx) < 0
                const leg = txLegLabel(tx)
                return (
                  <tr key={tx.id} className="border-b border-gray/200 last:border-0">
                    <td className="px-5 py-4">
                      <div className="text-sm-medium text-gray/900">{txTypeLabel(tx.type, tx.source_type)}</div>
                      {leg ? <div className="text-xs-regular text-gray/600">{leg}</div> : null}
                    </td>
                    <td className="px-5 py-4 text-sm-regular text-gray/600">
                      {tx.display_code || '—'}
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-sm-medium text-gray/900">{when.date}</div>
                      <div className="text-sm-regular text-gray/600">{when.time}</div>
                    </td>
                    <td
                      className={`whitespace-nowrap px-5 py-4 text-right text-sm-medium ${
                        negative ? 'text-gray/900' : 'text-success/700'
                      }`}
                    >
                      {formatSignedRp(tx)}
                    </td>
                    <td className="px-5 py-4">
                      <StatusBadge status={tx.status} />
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-right text-sm-medium text-gray/900">
                      {formatRp(tx.balance_after_idr)}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ───────────────────────────────────────────────── */}
      {totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3 border-t border-gray/200 px-5 py-4">
          <span className="text-sm-medium text-gray/700">
            Page {paginator.page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <MyButton
              color="secondary"
              variant="outlined"
              size="sm"
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <span className="text-sm-semibold">Previous</span>
            </MyButton>
            <MyButton
              color="secondary"
              variant="outlined"
              size="sm"
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              <span className="text-sm-semibold">Next</span>
            </MyButton>
          </div>
        </div>
      ) : null}
    </section>
  )
}

SaldoHistory.propTypes = {
  companyId: PropTypes.string,
  refreshKey: PropTypes.number,
}

SaldoHistory.defaultProps = {
  companyId: null,
  refreshKey: 0,
}

export default SaldoHistory
